import React, { useEffect, useState, useRef, useCallback } from "react";
import { PipelineNodeData, HandleConfig } from "../../types";
import { Position } from "reactflow";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager";
// Import the shared suggestions
import { PIPELINE_SUGGESTIONS, CUSTOM_LABEL_SUGGESTIONS } from "../../data/suggestions"; // Add CUSTOM_LABEL_SUGGESTIONS

interface PipelineConfigBoxProps {
  nodeId: string;
  data: PipelineNodeData;
  onUpdate?: (data: Partial<PipelineNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

export default function PipelineConfigBox({
  data,
  nodeId,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  portalRoot,
  isAdvancedMode = false,
}: PipelineConfigBoxProps) {
  // Initialize state with props, defaulting name to "Pipeline" if not provided
  const [name, setName] = useState(data.name ?? "Pipeline");
  const [label, setLabel] = useState(data.label || "");
  const [writekey, setWritekey] = useState(data.writekey || "");
  const [notes, setNotes] = useState(data.notes || "");

  const [nameVisibility, setNameVisibility] = useState<VisibilityOption>(data.nameVisibility ?? "visible");
  const [labelVisibility, setLabelVisibility] = useState<VisibilityOption>(data.labelVisibility ?? "visible");
  const [writeKeyVisibility, setWriteKeyVisibility] = useState<VisibilityOption>(data.writeKeyVisibility ?? "visible");
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(data.notesVisibility ?? "visible");

  // Default handles: one input (left), one output (right)
  const defaultHandles: HandleConfig[] = [
    { id: 'default-target', type: 'target', position: Position.Left, color: '#22c55e' },   // Green
    { id: 'default-source', type: 'source', position: Position.Right, color: '#3b82f6' }, // Blue
  ];

  const [handles, setHandles] = useState<HandleConfig[]>(
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );

  const isFirstRender = useRef(true);
  const prevUpdateRef = useRef(""); // Last update sent to parent
  const prevPropsRef = useRef("");  // Last props received from parent

  useEffect(() => {
    const incomingProps = JSON.stringify({
      name: data.name ?? "Pipeline",
      label: data.label || "",
      writekey: data.writekey || "",
      notes: data.notes || "",
      nameVisibility: data.nameVisibility ?? "visible",
      labelVisibility: data.labelVisibility ?? "visible",
      writeKeyVisibility: data.writeKeyVisibility ?? "visible",
      notesVisibility: data.notesVisibility ?? "visible",
      handles: Array.isArray(data.handles) ? data.handles : defaultHandles,
    });

    if (incomingProps !== prevPropsRef.current) {
      prevPropsRef.current = incomingProps;

      setName(data.name ?? "Pipeline");
      setLabel(data.label || "");
      setWritekey(data.writekey || "");
      setNotes(data.notes || "");
      setNameVisibility(data.nameVisibility ?? "visible");
      setLabelVisibility(data.labelVisibility ?? "visible");
      setWriteKeyVisibility(data.writeKeyVisibility ?? "visible");
      setNotesVisibility(data.notesVisibility ?? "visible");

      if (Array.isArray(data.handles)) {
        setHandles([...data.handles]);
      } else {
        setHandles(defaultHandles);
      }
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [data]);

  useEffect(() => {
    if (isFirstRender.current) return;

    const outgoingUpdate = JSON.stringify({
      name,
      label,
      writekey,
      notes,
      nameVisibility,
      labelVisibility,
      writeKeyVisibility,
      notesVisibility,
      handles,
    });

    if (outgoingUpdate !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = outgoingUpdate;
      onUpdate({
        name,
        label,
        writekey,
        notes,
        nameVisibility,
        labelVisibility,
        writeKeyVisibility,
        notesVisibility,
        handles,
      });
    }
  }, [
    name,
    label,
    writekey,
    notes,
    nameVisibility,
    labelVisibility,
    writeKeyVisibility,
    notesVisibility,
    handles,
    onUpdate,
  ]);

  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);

  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Pipeline Name {/* This field should control the 'name' state */}
        </label>
        <AutoComplete
          value={name} // Use 'name' state here
          onChange={(value: string) => {
            setName(value); // Update 'name' state
          }}
          onSelect={(option) => {
            if (typeof option === 'object') {
              setName(option.label); // Update 'name' state
              setWritekey(option.value);
            } else {
              setName(option); // Update 'name' state
              setWritekey(option);
            }
          }}
          options={PIPELINE_SUGGESTIONS}
          placeholder="Select or enter pipeline name"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Custom Label"
          visibility={labelVisibility}
          onToggleVisibility={() => toggleVisibility(labelVisibility, setLabelVisibility)}
          className="mb-1"
        />
        {/* Replace input with AutoComplete */}
        <AutoComplete
          value={label} // Use 'label' state here
          onChange={setLabel} // Update 'label' state
          options={CUSTOM_LABEL_SUGGESTIONS} // Use shared custom label suggestions
          placeholder="Set a Custom Label (optional)" // Updated placeholder
          dropdownClassName="absolute z-[99]" // Adjust z-index if needed
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="WriteKey"
          visibility={writeKeyVisibility}
          onToggleVisibility={() => toggleVisibility(writeKeyVisibility, setWriteKeyVisibility)}
          className="mb-1"
        />
        <input
          type="text"
          value={writekey}
          onChange={e => setWritekey(e.target.value)}
          placeholder="Enter WriteKey"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this pipeline (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>
      {/* --- Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId}
          handles={handles}
          onChange={handleHandlesChange}
          maxHandles={6}
          portalRoot={portalRoot}
        />
      </div>
    </div>
  );
}
